import { Permission } from 'Generated/schema';
import { SCHEDULED_QUERY_LIMITS_DOC_URL, SNOWFLAKE_DOC_URL, SPLIT_FLAGS } from 'Source/constants';
import urls from 'Source/urls';

export const SETTINGS_SIDEBAR_WIDTH = 252;
export const SETTINGS_CONTENT_MAX_WIDTH = 1200;

export interface SettingsItem {
  /** Stable id used for keys, tids, and aria. */
  id: string;
  /** Visible label in the sidebar. Also doubles as the page header. */
  label: string;
  /** Destination URL when the item is clicked. */
  to: string;
  /** Permissions required to see this item. ANY-of semantics. */
  permissions: Permission[];
  /** Optional URL prefixes (other than `to`) that should also mark this item active. */
  activePrefixes?: string[];
  /** Optional subheader for the page header (one sentence). */
  subheader?: string;
  /** Optional "Read more in our documentation" link rendered in the header. */
  docsHref?: string;
  /** Optional feature flag that must be on for the item to appear. */
  featureFlag?: keyof typeof SPLIT_FLAGS;
  /**
   * Optional Panther-AI gate. When true, the item is hidden unless
   * `useAIEnabled()` resolves to true (Panther-AI enabled for tenant
   * + user has `RunPantherAi`). Matches the legacy ProfileSettings
   * behavior where the AI Preferences tab disappeared with AI off.
   */
  requiresAIEnabled?: boolean;
  /**
   * Optional remote-MCP gate. When true, the item is hidden unless
   * `useAppConfig().remoteMcpEnabled` is on. Matches the legacy
   * AISettings tab gating on `appConfig.remoteMcpEnabled`.
   */
  requiresRemoteMcp?: boolean;
}

export interface SettingsGroup {
  id: string;
  label: string;
  items: SettingsItem[];
}

/**
 * Sidebar groups + items, in render order, per the unified settings Figma.
 * Each item's `to` resolves to the route registered in `routes.tsx`;
 * `activePrefixes` covers any legacy URL that should still highlight the
 * item (e.g. `?section=` deep-links from before the IA flatten).
 */
export const SETTINGS_GROUPS: SettingsGroup[] = [
  {
    id: 'general',
    label: 'General Settings',
    items: [
      {
        id: 'main-info',
        label: 'Main Info & Preferences',
        subheader: 'General account settings, organization details, and platform preferences.',
        to: urls.settings.general.mainInfo(),
        // The legacy `/settings/general/` (no sub-route) still resolves to
        // this item for any pre-stack-2 deep links.
        activePrefixes: [urls.settings.general.home()],
        // No permission gate: matches the page itself — any authenticated
        // user must be able to view + edit their own profile. Restricted
        // roles see the company-info inputs disabled.
        permissions: [],
      },
      {
        id: 'theme',
        label: 'Theme',
        subheader: 'Choose how Panther looks for you.',
        to: urls.settings.general.theme(),
        permissions: [],
      },
      {
        id: 'ai-preferences',
        label: 'AI Preferences',
        subheader: 'Tune how Panther AI behaves for you.',
        to: urls.settings.general.aiPreferences(),
        permissions: [],
        requiresAIEnabled: true,
      },
    ],
  },
  {
    id: 'auth',
    label: 'Access & Authentication',
    items: [
      {
        id: 'password',
        label: 'Password Management',
        subheader: 'Change your password and review your password policy.',
        to: urls.settings.auth.password(),
        permissions: [],
      },
      {
        id: 'identity',
        label: 'Identity & Access',
        subheader: 'Configure SAML / SSO and the default role for new users.',
        to: urls.settings.auth.identity(),
        // Old `?section=saml` deep-links still resolve to this item.
        activePrefixes: [`${urls.settings.general.home()}?section=saml`],
        permissions: [Permission.GeneralSettingsRead, Permission.GeneralSettingsModify],
      },
      {
        id: 'users',
        label: 'User Management',
        subheader: 'Invite, manage, and remove users in your organization.',
        to: urls.settings.users(),
        permissions: [Permission.UserRead, Permission.UserModify],
      },
      {
        id: 'roles',
        label: 'Roles & Permissions',
        subheader: 'Define which actions each role can take.',
        to: urls.settings.roles.list(),
        permissions: [Permission.UserRead, Permission.UserModify],
      },
    ],
  },
  {
    id: 'data-lake',
    label: 'Data Lake',
    items: [
      {
        // URL path keeps the historical `snowflake` slug to avoid breaking
        // existing bookmarks. The label is data-lake-neutral because the
        // page also serves Databricks customers (body branches on
        // `pantherDataLakeType`).
        id: 'snowflake',
        label: 'Data Lake Configuration',
        subheader: 'Data-lake connection details for this instance.',
        docsHref: SNOWFLAKE_DOC_URL,
        to: urls.settings.dataLake.snowflake(),
        // Legacy `?section=data-lake` deep-links resolve to this item.
        activePrefixes: [`${urls.settings.general.home()}?section=data-lake`],
        permissions: [Permission.GeneralSettingsRead, Permission.GeneralSettingsModify],
      },
      {
        id: 'scheduled-searches',
        label: 'Scheduled Searches',
        subheader: 'Require a LIMIT clause on every scheduled search.',
        docsHref: SCHEDULED_QUERY_LIMITS_DOC_URL,
        to: urls.settings.dataLake.scheduledSearches(),
        permissions: [Permission.GeneralSettingsRead, Permission.GeneralSettingsModify],
      },
    ],
  },
  {
    id: 'developer',
    label: 'Developer Tools',
    items: [
      {
        id: 'api-tokens',
        label: 'API Tokens',
        subheader: 'Create and manage API tokens for programmatic access.',
        to: urls.settings.api.list(),
        permissions: [Permission.OrganizationApiTokenRead, Permission.OrganizationApiTokenModify],
      },
      {
        id: 'api-playground',
        label: 'API Playground',
        subheader: 'Explore Panther APIs without leaving the console.',
        to: urls.settings.api.playground(''),
        permissions: [Permission.OrganizationApiTokenRead, Permission.OrganizationApiTokenModify],
      },
      {
        id: 'git-sync',
        label: 'Git Sync',
        subheader: 'Connect a repository to automatically sync detection content.',
        to: urls.settings.developer.gitSync(),
        // Legacy `?section=dev` deep-links resolve to this item.
        activePrefixes: [`${urls.settings.general.home()}?section=dev`],
        permissions: [Permission.GeneralSettingsRead, Permission.GeneralSettingsModify],
      },
      {
        id: 'analysis-tool',
        label: 'Panther Analysis Tool',
        subheader: 'Toggle whether detections are managed via the Panther Analysis Tool.',
        to: urls.settings.developer.analysisTool(),
        permissions: [Permission.GeneralSettingsRead, Permission.GeneralSettingsModify],
      },
    ],
  },
  {
    id: 'ai',
    label: 'AI & Automation',
    items: [
      {
        id: 'usage',
        label: 'Usage',
        subheader: 'Track AI usage against your contracted limits.',
        to: urls.settings.usage(),
        permissions: [Permission.GeneralSettingsRead],
        featureFlag: 'USAGE_DASHBOARD',
      },
      {
        id: 'configuration',
        label: 'Configuration',
        subheader:
          'Tune Panther AI for your organization — auto-run, risk scoring, and benign / risky labels.',
        to: urls.settings.ai.configuration(),
        // `/alert-triage/` redirects into the bundled Configuration page
        // and should highlight this item too. `/settings/ai/` is the
        // AI-home redirect, not Configuration — listing it would
        // briefly show "Configuration" in the header for users who end
        // up on Skills or MCP Servers.
        activePrefixes: [urls.settings.ai.alertTriage()],
        permissions: [Permission.GeneralSettingsRead, Permission.GeneralSettingsModify],
      },
      {
        id: 'web-access',
        label: 'Web Access',
        subheader: 'Allow Panther AI to fetch information from approved domains.',
        to: urls.settings.ai.webAccess(),
        permissions: [Permission.GeneralSettingsRead, Permission.GeneralSettingsModify],
      },
      {
        id: 'remote-mcp',
        label: 'Remote MCP',
        subheader: 'Approved redirect domains and custom schemes for the MCP OAuth flow.',
        to: urls.settings.ai.remoteMcp(),
        permissions: [Permission.GeneralSettingsRead, Permission.GeneralSettingsModify],
        requiresRemoteMcp: true,
      },
      {
        id: 'mcp-servers',
        label: 'MCP Servers',
        subheader: 'Connect and manage external MCP server integrations.',
        to: urls.settings.mcp.list(),
        // Permission-gated only (no feature-flag gate): matches the legacy
        // AISettings tab, which showed MCP Servers to anyone with the
        // permission and let `McpSectionV3` handle `ENABLE_AI_MCP_SERVERS`
        // internally (graceful disabled state, not a hidden item).
        permissions: [Permission.McpServerRead, Permission.McpServerModify],
      },
      {
        id: 'skills',
        label: 'Skills',
        subheader: 'Author reusable AI skills and govern who can run them.',
        to: urls.settings.aiSkills.list(),
        permissions: [Permission.ViewAiSkills, Permission.ManageAiSkills],
        featureFlag: 'ENABLE_AI_SKILLS',
      },
      // NOTE: Figma's "Policies" sub-item under AI & Automation is omitted
      // intentionally — the feature has not been implemented yet, so the
      // sidebar should not point at a dead route. Re-add when the
      // backing code lands.
    ],
  },
  {
    id: 'alerts',
    label: 'Alert Configuration',
    items: [
      {
        id: 'context-tags',
        label: 'Alert Context Tags',
        subheader: 'Tags surfaced on alerts to help triagers prioritize and route work.',
        to: urls.settings.alerts.contextTags(),
        permissions: [Permission.AlertRead, Permission.AlertModify],
      },
    ],
  },
  {
    id: 'about',
    label: 'About',
    items: [
      {
        id: 'oss-licenses',
        label: 'Open Source Licenses',
        subheader: 'Attribution and license text for the open source software Panther uses.',
        to: urls.settings.about.licenses(),
        // Informational page — visible to every authenticated user.
        permissions: [],
      },
    ],
  },
];
